// The body carries one of these two CMS Design System theme classes.
export const LIGHT_THEME = 'ds-base';
export const DARK_THEME = 'ds-base--inverse';

export type ThemeClass = typeof LIGHT_THEME | typeof DARK_THEME;

// Persisted in localStorage as JSON `{ oldValue, newValue }` (class names).
export const MODE_CLASS_KEY = 'mode-class';

interface ModeClassRecord {
  oldValue: ThemeClass;
  newValue: ThemeClass;
}

function persist(oldValue: ThemeClass, newValue: ThemeClass): void {
  localStorage.removeItem(MODE_CLASS_KEY);
  localStorage.setItem(MODE_CLASS_KEY, JSON.stringify({ oldValue, newValue } satisfies ModeClassRecord));
}

/** The theme currently written to `mode-class`, or `null` if it is unset/unparsable. */
export function readPersistedTheme(): ThemeClass | null {
  const raw = localStorage.getItem(MODE_CLASS_KEY);
  if (raw == null) return null;
  try {
    return (JSON.parse(raw) as ModeClassRecord).newValue === DARK_THEME ? DARK_THEME : LIGHT_THEME;
  } catch {
    return null;
  }
}

/**
 * Resolve the theme for this visit. On a first visit (no stored value) this
 * seeds `mode-class` from `prefers-color-scheme`; on a return visit it just
 * reads the stored value and writes nothing. Ported from `AppComponent.ngOnInit`.
 */
export function resolveInitialTheme(): ThemeClass {
  const stored = readPersistedTheme();
  if (stored !== null) return stored;

  if (window.matchMedia('(prefers-color-scheme: dark)').media === 'not all') {
    // prefers-color-scheme unsupported -> light
    persist(DARK_THEME, LIGHT_THEME);
    return LIGHT_THEME;
  }

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  if (!mediaQuery || mediaQuery.matches) {
    persist(DARK_THEME, DARK_THEME);
    return DARK_THEME;
  }
  persist(DARK_THEME, LIGHT_THEME);
  return LIGHT_THEME;
}

/** Swap the body's theme class for `theme`. */
export function applyThemeClass(theme: ThemeClass): void {
  const other = theme === DARK_THEME ? LIGHT_THEME : DARK_THEME;
  document.body.classList.remove(other);
  document.body.classList.add(theme);
}

/** Write a toggle to storage and broadcast it (same-tab listeners + other tabs). */
export function persistThemeToggle(previous: ThemeClass, next: ThemeClass): void {
  persist(previous, next);
  window.dispatchEvent(
    new StorageEvent('storage', {
      key: MODE_CLASS_KEY,
      newValue: JSON.stringify({ oldValue: previous, newValue: next } satisfies ModeClassRecord),
    }),
  );
}
